import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

interface SerialNumberReservation {
  serialNumber: string
  label: string
}

type ReservationsByTileKey = Record<string, SerialNumberReservation>

interface PowerMeterSerialNumberReservationContextValue {
  reservations: ReservationsByTileKey
  setReservation: (tileKey: string, reservation: SerialNumberReservation | null) => void
}

const PowerMeterSerialNumberReservationContext = createContext<PowerMeterSerialNumberReservationContextValue>({
  reservations: {},
  setReservation: () => undefined,
})

/**
 * Shares serial numbers currently selected (but not yet saved) across all power meter forms rendered for the
 * same installation, so the same serial number cannot be picked in two open, unsaved forms at once.
 */
export const PowerMeterSerialNumberReservationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [reservations, setReservations] = useState<ReservationsByTileKey>({})

  const setReservation = useCallback((tileKey: string, reservation: SerialNumberReservation | null): void => {
    setReservations(previous => {
      const isSame = previous[tileKey]?.serialNumber === reservation?.serialNumber && previous[tileKey]?.label === reservation?.label
      if (isSame) return previous

      if (!reservation) {
        return Object.fromEntries(Object.entries(previous).filter(([key]) => key !== tileKey))
      }

      return { ...previous, [tileKey]: reservation }
    })
  }, [])

  const value = useMemo(() => ({ reservations, setReservation }), [reservations, setReservation])

  return <PowerMeterSerialNumberReservationContext.Provider value={value}>{children}</PowerMeterSerialNumberReservationContext.Provider>
}

interface UsePowerMeterSerialNumberReservationResult {
  /** Serial numbers currently selected in other, still unsaved power meter forms, keyed by that form's label. */
  reservedSerialNumbersByOtherTiles: Record<string, string>
  /** Reports (or clears, when `null`) the serial number currently selected in this form so other forms can see it. */
  reserveSerialNumber: (serialNumber: string | null, label: string) => void
}

export const usePowerMeterSerialNumberReservation = (tileKey: string): UsePowerMeterSerialNumberReservationResult => {
  const { reservations, setReservation } = useContext(PowerMeterSerialNumberReservationContext)

  const reservedSerialNumbersByOtherTiles = useMemo(() => {
    const map: Record<string, string> = {}

    for (const [key, reservation] of Object.entries(reservations)) {
      if (key === tileKey) continue
      map[reservation.serialNumber] = reservation.label
    }

    return map
  }, [reservations, tileKey])

  const reserveSerialNumber = useCallback(
    (serialNumber: string | null, label: string): void => {
      setReservation(tileKey, serialNumber ? { serialNumber, label } : null)
    },
    [setReservation, tileKey]
  )

  return { reservedSerialNumbersByOtherTiles, reserveSerialNumber }
}
